import * as React from 'react';
import { FormControl, FormItem, FormMessage } from '../form';
import { Label } from '../label';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Button } from '../button';
import { cn } from 'lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { Calendar } from '../calendar';

interface DatePickerGsProps {
  label?: string;
  fieldValue: Date | undefined;
  fieldOnChange: (...event: any[]) => void;
  minDate?: Date;
  maxDate?: Date;
  showMonthYearPicker?: boolean; // NOVA PROP
  isLoading?: boolean;
  disabled?: boolean;
}

const DatePickerGs = React.forwardRef<HTMLBaseElement, DatePickerGsProps>(
  (
    {
      label,
      minDate,
      maxDate,
      fieldValue,
      fieldOnChange,
      showMonthYearPicker,
      isLoading = false,
      disabled,
    },
    ref
  ) => {
    const [month, setMonth] = React.useState<Date>(fieldValue || new Date());

    const years = Array.from(
      {
        length: (maxDate?.getFullYear() || new Date().getFullYear()) - 1900 + 1,
      },
      (_, i) => 1900 + i
    );
    const months = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ];

    // Atualiza fieldOnChange quando mês/ano mudam (modo MonthYearPicker)
    React.useEffect(() => {
      if (showMonthYearPicker) {
        fieldOnChange(month);
      }
    }, [month]);

    return (
      <FormItem>
        <div className="grid w-full items-center gap-1.5">
          {label && (
            <Label className="font-GilroySemibold text-sm text-slate-800">
              {label}
            </Label>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  variant={'outline'}
                  className={cn(
                    'font-sans pl-3 text-left border-solid font-normal focus-visible:ring-primary-500',
                    !fieldValue && 'text-muted-foreground'
                  )}
                  disabled={isLoading || disabled}
                >
                  {fieldValue ? (
                    format(fieldValue, "dd'/'MM'/'yyyy", { locale: ptBR })
                  ) : (
                    <span>Selecione ...</span>
                  )}
                  {isLoading ? (
                    <span className="ml-auto">
                      <svg
                        className="animate-spin h-4 w-4 text-slate-400"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        />
                      </svg>
                    </span>
                  ) : (
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  )}
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={fieldValue}
                onSelect={fieldOnChange}
                disabled={(date) =>
                  date > (maxDate || new Date()) ||
                  date < (minDate || new Date('1900-01-01'))
                }
                initialFocus
                month={month}
                onMonthChange={setMonth}
                locale={ptBR}
                components={
                  showMonthYearPicker
                    ? {
                        Caption: ({ displayMonth }) => (
                          <div className="flex items-center justify-center gap-3 p-2 border-b border-slate-200 mb-2">
                            <select
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition"
                              value={displayMonth.getMonth()}
                              onChange={(e) => {
                                const newMonth = new Date(displayMonth);
                                newMonth.setMonth(Number(e.target.value));
                                setMonth(newMonth);
                              }}
                            >
                              {months.map((m, idx) => (
                                <option key={m} value={idx}>
                                  {m}
                                </option>
                              ))}
                            </select>
                            <select
                              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition"
                              value={displayMonth.getFullYear()}
                              onChange={(e) => {
                                const newMonth = new Date(displayMonth);
                                newMonth.setFullYear(Number(e.target.value));
                                setMonth(newMonth);
                              }}
                            >
                              {years.map((y) => (
                                <option key={y} value={y}>
                                  {y}
                                </option>
                              ))}
                            </select>
                          </div>
                        ),
                      }
                    : undefined
                }
              />
            </PopoverContent>
          </Popover>
          <FormMessage />
        </div>
      </FormItem>
    );
  }
);

DatePickerGs.displayName = 'DatePickerGs';
export default DatePickerGs;
